/**
 * @auth 赖传峰
 * @date 2022/3/31
 */
import { DisplayMode, useCookie } from '#imports'
import { fileSizeSort, isEpisodeShareFiles, naturalSort } from '@/store/share.script'
import { type ShareCommonParams, type UndertakeType } from '@/track/webShare'
import { isVideo } from '@/utils'
import qs from 'qs'
import { type RouteLocationNormalized } from 'vue-router'
import useDisplayMode from '~/composables/useDisplayMode'
import { useShare } from '~/store/share'
import { COOKIE_OPTIONS } from '~/types/constants'
import {
  DriveKind,
  FileOrder,
  RoutePathOfDrive,
  RouteQueryKeyActionValue,
  SceneInQuery4Track,
  StorageKey,
  ViewType,
} from '~/types/enum'

// NOTE: single 不能够强制指定
export const isValidViewType = (value: string): value is ViewType =>
  [ViewType.LIST, ViewType.GRID, ViewType.FLOW].includes(value as ViewType)

// NOTE: 非 setup 环境中, 获取 viewType 的方法
// setup 环境中可以通过 `const { viewType } = useDisplayMode()` 获取
export const getViewTypeValue = (): ViewType => {
  const viewType = useCookie<ViewType>(StorageKey.SSR_VIEW_TYPE, COOKIE_OPTIONS).value
  return viewType || DisplayMode.DEFAULT_DISPLAY_MODE.viewType
}

export const isFolder = (val: DriveKind): boolean => val === DriveKind.FOLDER

export const highlightFilesInDriveLink = (fileIds?: string[]) => {
  const query = {
    action: RouteQueryKeyActionValue.HIGHLIGHT_FILES,
    file_ids: fileIds,
  }
  return `${RoutePathOfDrive.RECENT}?${qs.stringify(query, { arrayFormat: 'repeat' })}`
}

export const openAndHighlightFileInDriveLink = (fileId: string) => {
  const query = {
    action: [RouteQueryKeyActionValue.OPEN_FILE, RouteQueryKeyActionValue.HIGHLIGHT_FILES],
    file_ids: [fileId],
    scene: SceneInQuery4Track.SHARE_PAGE, // 用于播放器埋点
  }

  return `${RoutePathOfDrive.RECENT}?${qs.stringify(query, { arrayFormat: 'repeat' })}`
}

/* 进入二级自动排序和自动播放承接流程 */
export const handleAutoSort = (route: RouteLocationNormalized) => {
  const shareStore = useShare()
  // 没有自动拉起播放，不执行以下逻辑
  if (shareStore.data && shareStore.data.params?.auto_play !== 'true') return
  // 根目录不是一个文件，不做承接
  if (shareStore.data?.files?.length !== 1) return

  let shareDetail
  const parentIds = route.params?.parent_ids

  if (parentIds && parentIds.length > 1) {
    shareStore.tempOrder = undefined
    return
  }

  if (parentIds?.length === 1) {
    // 在二级页面
    const currentParentId = parentIds[parentIds.length - 1]
    shareDetail = shareStore.detail[currentParentId]
  } else {
    // 在根目录
    shareDetail = shareStore.data
  }

  if (!shareDetail?.files || !shareDetail.files.length) return
  const { orderType } = useDisplayMode()
  // 满足剧集文件条件且未手动更改过排序，进行自动排序
  if (isEpisodeShareFiles(shareDetail.files) && !orderType.value) {
    shareDetail.files = naturalSort(shareDetail.files)
    shareStore.tempOrder = FileOrder.NAME_ASC
  } else if (shareDetail.files.length < 20 && !shareDetail.files.every(el => isFolder(el.kind)) && !orderType.value) {
    // 判断是否是磁力文件：二级页面数据 < 20 条时，且页面内不是全部都为文件夹时，更改界面排序方式为从大到小
    shareDetail.files = fileSizeSort(shareDetail.files, FileOrder.SIZE_DESC)
    shareStore.tempOrder = FileOrder.SIZE_DESC
  }
  // 返回第一个视频文件
  return shareDetail.files.find(el => isVideo(el.file_extension, el.mime_type))
}

export const shareTrackParams = (needSavingScene?: boolean) => {
  const shareStore = useShare()
  const viewType = getViewTypeValue()
  let undertakeType: UndertakeType = 'normal'
  if (shareStore.data) {
    if (shareStore.data.params?.auto_enter_subdir === String(true)) {
      undertakeType = 'auto_jump'
    }
    if (shareStore.data.params?.auto_play === String(true)) {
      undertakeType = 'auto_play'
    }
  }
  const params: ShareCommonParams = {
    share_userid: shareStore.data?.user_info?.user_id || '',
    display_style: viewType === ViewType.LIST ? 'list_style' : 'card_style',
    undertake_type: undertakeType,
  }
  if (needSavingScene) {
    params.saving_scene = window.scenePreviewMedia ? 'preview' : 'share_file_list'
  }
  return params
}
